import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

// Overlay shown while the list is blurred. Injected once, on the
// element itself, and removed when the sort is done.
const SB_OVERLAY_CLASS = 'sb-sorting-overlay';

// Products with this tag are pulled to the front of the grid,
// including ones that live beyond the first page. Shopify serves
// /collections/<handle>/<tag> as a tag-filtered view of the same
// collection, so we fetch that and hoist its cards.
//
// Only tagged products that actually belong in the current results
// are hoisted. On search results, or when a filter is active and we
// can't confirm a product matches, nothing is added from outside the
// results. On search results, products that contain none of the search
// words are hidden, and only buyable products matching every search
// word are moved to the top.
const SB_TAG = 'allow-add-to-cart';

// Filler words ignored in a search ("kettle for kitchen" = kettle, kitchen).
const SB_STOP_WORDS = new Set(['the', 'and', 'for', 'with', 'of', 'in', 'on', 'to', 'an', 'or', 'by', 'at']);

// Safety cap on how many pages of the tagged view we walk.
const SB_MAX_TAG_PAGES = 10;

// Never keep the grid hidden longer than this, even if a fetch hangs.
// Keep in step with the CSS fallback delay (sb-reveal).
const SB_OVERLAY_TIMEOUT_MS = 6000;

// The tagged list is cached per tab for this long, so page 2, page 3
// and revisits don't wait on the network at all.
const SB_CACHE_TTL_MS = 5 * 60 * 1000;

// Bump the version whenever the card markup changes, so old cached cards are ignored.
const SB_CACHE_PREFIX = 'sb-tagged-v2:';

if (!document.getElementById('sb-sorting-overlay-style')) {
  const sbStyle = document.createElement('style');
  sbStyle.id = 'sb-sorting-overlay-style';
  sbStyle.textContent = `
    .${SB_OVERLAY_CLASS} {
      position: fixed;
      inset: 0;
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: center;
      pointer-events: none;
    }

    .${SB_OVERLAY_CLASS} span {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      background: #ef2b2d;
      color: #fff;
      padding: 13px 24px;
      border-radius: 999px;
      font-family: inherit;
      font-size: 0.875rem;
      font-weight: 700;
      letter-spacing: 0.04em;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
    }

    .${SB_OVERLAY_CLASS} span::before {
      content: '';
      width: 14px;
      height: 14px;
      border-radius: 50%;
      border: 2px solid rgba(255, 255, 255, 0.35);
      border-top-color: #fff;
      animation: sb-spin 700ms linear infinite;
    }

    @keyframes sb-spin {
      to { transform: rotate(360deg); }
    }

    @media (prefers-reduced-motion: reduce) {
      .${SB_OVERLAY_CLASS} span::before {
        animation: none;
      }
    }
  `;
  document.head.appendChild(sbStyle);
}

/**
 * A custom element that renders a paginated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {HTMLElement | null} */
  #sbOverlay = null;
  /** @type {MutationObserver | null} */
  #sbObserver = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #sbTimeout = null;
  /** The grid we last hoisted into; a new one means filters/sort re-rendered it. */
  /** @type {Element | null} */
  #sbGrid = null;
  #sbRunning = false;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    if (this.dataset.sbSort === 'false') {
      this.setAttribute('data-sb-ready', '');
      return;
    }

    this.#sbShowOverlay();
    this.#sortPricedFirst();

    // Safety net: unblur even if something below stalls.
    this.#sbTimeout = setTimeout(() => this.#sbHideOverlay(), SB_OVERLAY_TIMEOUT_MS);

    // Start as soon as the cards are in the DOM. Previously this waited
    // for window 'load', which waits for every image on the page and was
    // the main reason tagged products showed up late.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.#sbFinish(), { once: true });
    } else {
      this.#sbFinish();
    }
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);

    this.#sbHideOverlay();

    this.#sbObserver?.disconnect();
    this.#sbObserver = null;
  }

  /** Puts the "Loading and sorting" message on screen. */
  #sbShowOverlay() {
    if (this.#sbOverlay) return;

    const overlay = document.createElement('div');
    overlay.className = SB_OVERLAY_CLASS;
    overlay.innerHTML = '<span>Loading and sorting</span>';

    document.body.appendChild(overlay);
    this.#sbOverlay = overlay;
  }

  #sbHideOverlay() {
    if (this.#sbTimeout) clearTimeout(this.#sbTimeout);
    this.#sbTimeout = null;

    this.setAttribute('data-sb-ready', '');
    this.#sbOverlay?.remove();
    this.#sbOverlay = null;
  }

  /** Current ?page= number (1 when absent). */
  #sbCurrentPage() {
    const page = Number(new URLSearchParams(window.location.search).get('page'));
    return Number.isFinite(page) && page > 1 ? page : 1;
  }

  /**
   * True on search results: the /search page (including localized
   * /xx/search) or any page with a search term in the URL. The tag view
   * ignores search terms, so nothing may be pulled in from it here.
   */
  #sbIsSearch() {
    const path = window.location.pathname.replace(/\/+$/, '');
    if (/(^|\/)search$/.test(path)) return true;

    const params = new URLSearchParams(window.location.search);
    return params.has('q') || params.has('terms');
  }

  /** True when any storefront filter (filter.*) is applied. */
  #sbHasFilters() {
    for (const key of new URLSearchParams(window.location.search).keys()) {
      if (key.startsWith('filter.')) return true;
    }
    return false;
  }

  /**
   * Fetches every page of the tag-filtered view of this collection,
   * keeping the shopper's current sort and filters.
   *
   * @returns {Promise<{ items: Element[], complete: boolean }>}
   */
  async #sbFetchTagged() {
    const empty = { items: [], complete: false };
    if (this.#sbIsSearch()) return empty;

    const path = window.location.pathname.replace(/\/+$/, '');
    const parts = path.split('/');

    // Only on a plain collection page (/collections/<handle>), not on
    // a tag-filtered view (which would already be /collections/<handle>/<tag>).
    if (parts[1] !== 'collections' || parts.length !== 3) return empty;

    const sectionId = this.getAttribute('section-id');
    if (!sectionId) return empty;

    const base = parts.join('/');
    const params = new URLSearchParams(window.location.search);
    params.delete('page');
    params.set('section_id', sectionId);

    // Same collection + sort + filters = same tagged list, whatever page we're on.
    const cacheKey = `${SB_CACHE_PREFIX}${base}?${params}`;
    const cached = this.#sbReadCache(cacheKey);
    if (cached) return cached;

    const result = await this.#sbFetchTaggedPages(base, params);
    if (result.items.length > 0) this.#sbWriteCache(cacheKey, result);
    return result;
  }

  /**
   * @param {string} key
   * @returns {{ items: Element[], complete: boolean } | null}
   */
  #sbReadCache(key) {
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;

      const data = JSON.parse(raw);
      if (!data || Date.now() - data.t > SB_CACHE_TTL_MS || !Array.isArray(data.html)) {
        sessionStorage.removeItem(key);
        return null;
      }

      const template = document.createElement('template');
      template.innerHTML = data.html.join('');
      return { items: Array.from(template.content.children), complete: !!data.complete };
    } catch {
      return null;
    }
  }

  /**
   * @param {string} key
   * @param {{ items: Element[], complete: boolean }} result
   */
  #sbWriteCache(key, { items, complete }) {
    try {
      const html = items.map((li) => li.outerHTML);
      sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), complete, html }));
    } catch {
      // Storage full or blocked: carry on without caching.
    }
  }

  /**
   * @param {string} base
   * @param {URLSearchParams} params
   * @returns {Promise<{ items: Element[], complete: boolean }>}
   */
  async #sbFetchTaggedPages(base, params) {
    /** @type {Element[]} */
    const items = [];
    const seen = new Set();
    let firstPageSize = 0;

    for (let page = 1; page <= SB_MAX_TAG_PAGES; page++) {
      params.set('page', String(page));

      let html = '';
      try {
        const response = await fetch(`${base}/${SB_TAG}?${params}`);
        if (!response.ok) return { items, complete: false };
        html = await response.text();
      } catch {
        return { items, complete: false };
      }

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const found = doc.querySelectorAll('li.product-grid__item');

      let added = 0;
      for (const li of found) {
        const id = li instanceof HTMLElement ? li.dataset.productId : undefined;
        if (!id || seen.has(id)) continue;
        seen.add(id);
        items.push(li);
        added++;
      }

      if (page === 1) firstPageSize = found.length;

      // Last page reached: nothing new, or a short page.
      if (added === 0 || found.length < firstPageSize) return { items, complete: true };
    }

    // Hit the cap; there may be more tagged products we didn't get.
    return { items, complete: false };
  }

  /**
   * The ids of every product in the current results (collection + current
   * filters), from the sb-index view. The tag view doesn't reliably apply
   * filters, so this is what decides whether a tagged product belongs.
   *
   * @returns {Promise<Set<string> | null>} null when it can't be worked out
   */
  async #sbAllowedIds() {
    const base = this.#sbCollectionBase();
    if (!base) return null;

    const index = await this.#sbFetchIndex(base);
    if (!index) return null;

    return new Set(index.filter((item) => !item.hidden).map((item) => item.id));
  }

  /**
   * Page 1: prepends every tagged product that isn't already shown and
   * that belongs in the current results.
   * Page 2+: removes tagged products, since they now live on page 1
   * (otherwise they'd appear twice across the pages).
   */
  async #sbHoistTagged() {
    // Search results: never add products the search didn't return.
    if (this.#sbIsSearch()) return;

    const grid = this.querySelector('ul.product-grid');
    if (!grid) return;
    this.#sbGrid = grid;

    const [{ items, complete }, allowed] = await Promise.all([this.#sbFetchTagged(), this.#sbAllowedIds()]);
    if (items.length === 0) return;

    // With filters on, only hoist what we can confirm matches them. If we
    // can't check, leave the grid alone (page 1 won't gain anything, so
    // page 2+ must not lose anything either).
    if (!allowed && this.#sbHasFilters()) return;

    const relevant = allowed
      ? items.filter((li) => allowed.has(String(/** @type {HTMLElement} */ (li).dataset.productId)))
      : items;
    if (relevant.length === 0) return;

    // The grid may have been re-rendered while we were fetching.
    if (!grid.isConnected) return;

    const cards = Array.from(grid.querySelectorAll('li.product-grid__item')).filter(
      (li) => li instanceof HTMLElement
    );

    if (this.#sbCurrentPage() === 1) {
      const present = new Set(cards.map((li) => /** @type {HTMLElement} */ (li).dataset.productId));
      const fragment = document.createDocumentFragment();

      for (const li of relevant) {
        const id = /** @type {HTMLElement} */ (li).dataset.productId;
        if (present.has(id)) continue;
        fragment.appendChild(document.importNode(li, true));
      }

      if (fragment.childElementCount > 0) grid.prepend(fragment);
    } else if (complete) {
      // Only safe to remove when we know page 1 holds all of them.
      const tagged = new Set(relevant.map((li) => /** @type {HTMLElement} */ (li).dataset.productId));
      for (const li of cards) {
        if (tagged.has(/** @type {HTMLElement} */ (li).dataset.productId)) li.remove();
      }
    }
  }

  /** /collections/<handle>, or null on any other page. */
  #sbCollectionBase() {
    const parts = window.location.pathname.replace(/\/+$/, '').split('/');
    return parts[1] === 'collections' && parts.length === 3 ? parts.join('/') : null;
  }

  /** Current sort + filters, without paging or rendering params. */
  #sbQuery() {
    const params = new URLSearchParams(window.location.search);
    for (const key of ['page', 'section_id', 'view']) params.delete(key);
    return params;
  }

  /**
   * The whole collection in its natural order (current sort + filters),
   * from the sb-index alternate template, cached per tab.
   *
   * @param {string} base
   * @returns {Promise<Array<{ id: string, tagged: boolean, since: number, hidden: boolean, raw: number }> | null>}
   */
  async #sbFetchIndex(base) {
    const params = this.#sbQuery();
    const cacheKey = `${SB_CACHE_PREFIX}index:${base}?${params}`;

    try {
      const cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
      if (cached && Date.now() - cached.t < SB_CACHE_TTL_MS && Array.isArray(cached.items)) return cached.items;
    } catch {}

    params.set('view', 'sb-index');

    /** @type {Array<[number, number, number, number]>} */
    const rows = [];
    let pages = 1;

    for (let page = 1; page <= pages && page <= 20; page++) {
      params.set('page', String(page));

      let data;
      try {
        const response = await fetch(`${base}?${params}`);
        if (!response.ok) return null;
        data = JSON.parse((await response.text()).trim());
      } catch {
        return null;
      }

      if (!data || !Array.isArray(data.items)) return null;
      pages = Number(data.pages) || 1;
      rows.push(...data.items);
    }

    const items = rows.map(([id, tagged, since, hidden], raw) => ({
      id: String(id),
      tagged: tagged === 1,
      since: Number(since) || 0,
      hidden: hidden === 1,
      raw,
    }));

    try {
      sessionStorage.setItem(cacheKey, JSON.stringify({ t: Date.now(), items }));
    } catch {}

    return items;
  }

  /**
   * Cards from one of Shopify's own pages of this collection.
   *
   * @param {string} base
   * @param {number} page
   * @returns {Promise<Element[]>}
   */
  async #sbFetchNaturalPage(base, page) {
    const sectionId = this.getAttribute('section-id');
    if (!sectionId) return [];

    const params = this.#sbQuery();
    params.set('page', String(page));
    params.set('section_id', sectionId);

    try {
      const response = await fetch(`${base}?${params}`);
      if (!response.ok) return [];
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      return Array.from(doc.querySelectorAll('li.product-grid__item'));
    } catch {
      return [];
    }
  }

  /**
   * Even pages: builds the full order (buyable products newest first, then
   * the rest in their normal order), cuts it into pages of the configured
   * size and shows this page's slice. Returns false if it couldn't, so the
   * caller can fall back.
   */
  async #sbEvenPage() {
    try {
      if (this.#sbIsSearch()) return false;

      const grid = this.querySelector('ul.product-grid');
      if (!grid) return false;
      this.#sbGrid = grid;

      const perPage = Number(
        /** @type {HTMLElement | null} */ (this.querySelector('[data-sb-per-page]'))?.dataset.sbPerPage
      );
      const base = this.#sbCollectionBase();
      if (!perPage || !base) return false;

      const index = await this.#sbFetchIndex(base);
      if (!index || index.length === 0) return false;

      const visible = index.filter((item) => !item.hidden);
      const tagged = visible.filter((item) => item.tagged).sort((a, b) => b.since - a.since);
      const ordered = tagged.concat(visible.filter((item) => !item.tagged));

      const totalPages = Math.max(1, Math.ceil(ordered.length / perPage));
      const page = this.#sbCurrentPage();

      // Shopify's pagination can show more pages than we need; send
      // anyone who lands past the end to the last real page.
      if (page > totalPages) {
        const url = new URL(window.location.href);
        url.searchParams.set('page', String(totalPages));
        window.location.replace(url);
        return true;
      }

      const slice = ordered.slice((page - 1) * perPage, page * perPage);

      /** @type {Map<string, Element>} */
      const cards = new Map();
      for (const li of grid.querySelectorAll('li.product-grid__item')) {
        const id = /** @type {HTMLElement} */ (li).dataset.productId;
        if (id) cards.set(id, li);
      }

      // Fetch only what this page is missing: buyable cards from the tag
      // view, the rest from whichever of Shopify's pages holds them.
      const missing = slice.filter((item) => !cards.has(item.id));
      /** @type {Promise<Element[]>[]} */
      const jobs = [];

      if (missing.some((item) => item.tagged)) {
        jobs.push(this.#sbFetchTagged().then((result) => result.items));
      }

      const naturalPages = new Set(
        missing.filter((item) => !item.tagged).map((item) => Math.floor(item.raw / perPage) + 1)
      );
      for (const naturalPage of naturalPages) jobs.push(this.#sbFetchNaturalPage(base, naturalPage));

      for (const list of await Promise.all(jobs)) {
        for (const li of list) {
          const id = /** @type {HTMLElement} */ (li).dataset.productId;
          if (id && !cards.has(id)) cards.set(id, li);
        }
      }

      // The grid may have been re-rendered while we were fetching.
      if (!grid.isConnected) return true;

      // Only products from this slice (i.e. the filtered results) are
      // placed; anything else the fetches returned is ignored.
      const fragment = document.createDocumentFragment();
      for (const item of slice) {
        const li = cards.get(item.id);
        if (!li) continue;
        fragment.appendChild(li.ownerDocument === document ? li : document.importNode(li, true));
      }

      for (const li of grid.querySelectorAll('li.product-grid__item')) li.remove();
      grid.appendChild(fragment);

      this.#sbTrimPagination(totalPages);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Hides page links (numbers and "next") past the last real page.
   *
   * @param {number} totalPages
   */
  #sbTrimPagination(totalPages) {
    for (const link of this.querySelectorAll('a[href*="page="]')) {
      const target = Number(new URL(/** @type {HTMLAnchorElement} */ (link).href, window.location.href).searchParams.get('page'));
      if (target > totalPages) {
        const item = link.closest('li') || link;
        /** @type {HTMLElement} */ (item).hidden = true;
      }
    }
  }

  /** Bring in tagged products, final sort, then drop the blur. */
  async #sbFinish() {
    if (this.#sbRunning) return;
    this.#sbRunning = true;

    // Hide the grid again while a filter/sort re-render is reorganised,
    // so the shopper never sees cards jump.
    this.removeAttribute('data-sb-ready');

    this.#sbObserver?.disconnect();

    try {
      // Search results: only reorder what the search returned, so buyable
      // matches go first and nothing unrelated is added.
      if (!this.#sbIsSearch()) {
        // With page numbers: build even pages. If that can't be done (or
        // infinite scroll is on), fall back to hoisting onto the first page.
        const paged = this.getAttribute('infinite-scroll') === 'false' && (await this.#sbEvenPage());
        if (!paged) await this.#sbHoistTagged();
      }
      this.#sortPricedFirst();
    } finally {
      this.#sbRunning = false;
      this.#sbHideOverlay();
      this.#sbWatch();
    }
  }

  /** Keeps the order correct after the initial load. */
  #sbWatch() {
    if (!this.#sbObserver) {
      this.#sbObserver = new MutationObserver(() => {
        if (this.#sbRunning) return;

        const grid = this.querySelector('ul.product-grid');

        // Filters / sort dropdown swapped in a fresh grid: hoist again.
        if (grid && grid !== this.#sbGrid) {
          this.#sbFinish();
          return;
        }

        this.#sbObserver?.disconnect();
        this.#sortPricedFirst();
        this.#sbConnectObserver();
      });
    }

    this.#sbConnectObserver();
  }

  /**
   * Watches the whole element (not just the current grid), so a grid
   * that gets replaced by a filter/sort re-render is still noticed.
   */
  #sbConnectObserver() {
    this.#sbObserver?.observe(this, { childList: true, subtree: true });
  }

  /**
   * Moves cards that show a price to the front of the grid.
   * Only products tagged `allow-add-to-cart` render a price.
   */
  #sortPricedFirst() {
    const grids = this.querySelectorAll('ul.product-grid');

    for (const grid of grids) {
      this.#sbHideUnmatched(grid);

      const items = Array.from(grid.children).filter(
        (node) => node instanceof HTMLElement && node.matches('li.product-grid__item')
      );

      if (items.length < 2) continue;

      // Newest-buyable first. data-sb-since is the Unix timestamp the
      // product got the tag (see Liquid). Missing/invalid = oldest.
      // Array.prototype.sort is stable, so ties keep their current order.
      // On search results, Shopify also returns loosely related products
      // (search "vacuum", get stoves and fridges). Those are hidden, and only
      // buyable products that match every search word are moved up.
      const terms = this.#sbSearchTerms();

      const priced = items
        .filter((item) => {
          const price = item.querySelector('[ref="priceContainer"] .price');
          if (!price || price.textContent.trim().length === 0) return false;
          return !terms || this.#sbMatchesSearch(item, terms, 'all');
        })
        .sort((a, b) => this.#sbSince(b) - this.#sbSince(a));

      // Anchor on the first card that stays put, so the reference
      // node is never one of the nodes being moved.
      const anchor = items.find((item) => !priced.includes(item));

      if (priced.length === 0 || !anchor) continue;

      // Skip if already in order, to avoid needless DOM churn.
      const alreadySorted = priced.every((item, i) => items[i] === item);
      if (alreadySorted) continue;

      const fragment = document.createDocumentFragment();
      for (const item of priced) fragment.appendChild(item);
      grid.insertBefore(fragment, anchor);
    }
  }

  /**
   * Lowercases, strips accents and splits into simple singular words,
   * so "Vacuums", "vacuum" and "Vacuüm" all compare equal.
   *
   * @param {string} text
   * @returns {string[]}
   */
  #sbWords(text) {
    return text
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .split(/[^a-z0-9]+/)
      .filter((word) => word.length > 1)
      .map((word) => {
        if (word.length > 4 && word.endsWith('ies')) return `${word.slice(0, -3)}y`;
        if (word.length > 4 && /(ches|shes|sses|xes|zes)$/.test(word)) return word.slice(0, -2);
        if (word.length > 3 && word.endsWith('s') && !word.endsWith('ss')) return word.slice(0, -1);
        return word;
      });
  }

  /**
   * The words searched for, or null when this isn't a search (or the
   * search has no usable words), in which case nothing is hidden and
   * every buyable card is moved up.
   *
   * @returns {string[] | null}
   */
  #sbSearchTerms() {
    if (!this.#sbIsSearch()) return null;

    const params = new URLSearchParams(window.location.search);
    const words = this.#sbWords(params.get('q') || params.get('terms') || '').filter(
      (word) => !SB_STOP_WORDS.has(word)
    );
    return words.length > 0 ? words : null;
  }

  /**
   * Checks the searched words against the card: its title and other
   * visible text, image alt text and product URL. A word counts when it
   * appears inside a card word, so "vac" matches "vacuum" and "fryer"
   * matches "airfryer".
   *
   * @param {Element} item
   * @param {string[]} terms
   * @param {'all' | 'any'} mode every word must match, or at least one
   */
  #sbMatchesSearch(item, terms, mode) {
    const parts = [item.textContent || ''];

    for (const img of item.querySelectorAll('img[alt]')) parts.push(img.getAttribute('alt') || '');

    for (const link of item.querySelectorAll('a[href*="/products/"]')) {
      const handle = (link.getAttribute('href') || '').split('/products/')[1]?.split(/[?#/]/)[0] || '';
      parts.push(handle.replace(/[-_]+/g, ' '));
      parts.push(link.getAttribute('aria-label') || '');
    }

    const cardWords = this.#sbWords(parts.join(' '));
    const found = (/** @type {string} */ term) => cardWords.some((word) => word.includes(term));
    return mode === 'all' ? terms.every(found) : terms.some(found);
  }

  /**
   * Search results only: hides products that contain none of the search
   * words (or part of one). Shows a short message if nothing is left.
   *
   * @param {Element} grid
   */
  #sbHideUnmatched(grid) {
    const terms = this.#sbSearchTerms();
    if (!terms) return;

    const cards = Array.from(grid.querySelectorAll('li.product-grid__item')).filter(
      (li) => li instanceof HTMLElement
    );

    let shown = 0;
    for (const li of cards) {
      const card = /** @type {HTMLElement} */ (li);
      if (this.#sbMatchesSearch(card, terms, 'any')) {
        shown++;
        if (card.hasAttribute('data-sb-excluded')) {
          card.removeAttribute('data-sb-excluded');
          card.style.removeProperty('display');
        }
      } else if (!card.hasAttribute('data-sb-excluded')) {
        card.setAttribute('data-sb-excluded', '');
        card.style.setProperty('display', 'none', 'important');
      }
    }

    const message = grid.querySelector('.sb-no-matches');
    if (cards.length > 0 && shown === 0) {
      if (!message) {
        const li = document.createElement('li');
        li.className = 'sb-no-matches';
        li.style.cssText = 'grid-column: 1 / -1; list-style: none; padding: 2rem 0; text-align: center;';
        li.textContent = 'No products match your search.';
        grid.appendChild(li);
      }
    } else {
      message?.remove();
    }
  }

  /**
   * When the product became buyable, as a Unix timestamp (0 if unknown).
   *
   * @param {Element} item
   */
  #sbSince(item) {
    const value = Number(item instanceof HTMLElement ? item.dataset.sbSince : 0);
    return Number.isFinite(value) ? value : 0;
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}